import {useMutation, useQuery} from "react-query"
import {Horse} from "../api/horse"
import {useSelector} from "react-redux"
import {RootState} from "../store"
import {TUser} from "../shared/types/user.type"
import {t} from "i18next"
import {UpdateHorseHeightDto} from "../dto/UpdateHorseHeightDto"
import {UpdateHorseWeightDto} from "../dto/UpdateHorseWeightDto"
import {hydraTransform} from "../shared/utils/hydraTransformer"
import {UpdateHorseHrDto} from "../dto/UpdateHorseHrDto"
import {UpdateHorseCommentDto} from "../dto/UpdateHorseCommentDto"
import {THorse} from "../shared/types/horse.type";

type UseHorseProps = {
    enableQuery: boolean,
    enableHeightsQuery?: boolean,
    enableWeightsQuery?: boolean,
    horseId?: number,
    horseStrId?: string,
    horseParams?: string[]
}

export const useHorse = ({
                             enableQuery,
                             horseId,
                             horseStrId,
                             enableHeightsQuery = false,
                             enableWeightsQuery = false,
                             horseParams = []
                         }: UseHorseProps) => {

    const user = useSelector<RootState>((state) => state.user.user) as TUser

    const horseQuery = useQuery(['horse', horseId], () => Horse.getHorse(horseId, horseParams), {
        enabled: enableQuery,
        keepPreviousData: true,

    })

    const horseHeightsQuery = useQuery(['horse_heights', horseStrId], () => Horse.getHorseHeights(horseStrId), {
        enabled: enableHeightsQuery,
        keepPreviousData: true,
    })

    const horseWeightsQuery = useQuery(['horse_weights', horseStrId], () => Horse.getHorseWeights(horseStrId), {
        enabled: enableWeightsQuery,
        keepPreviousData: true,
    })

    const updateHorseHeightMutation = useMutation((data: UpdateHorseHeightDto) => Horse.updateHorseHeight(data))
    const updateHorseWeightMutation = useMutation((data: UpdateHorseWeightDto) => Horse.updateHorseWeight(data))
    const updateHorseHrMutation = useMutation((data: UpdateHorseHrDto) => Horse.updateHorseHr(data))
    const updateHorseCommentMutation = useMutation((data: UpdateHorseCommentDto) => Horse.updateHorseComment(data))
    const updateHorseMutation = useMutation(({id, data}: {
        id: number, data: Partial<THorse>
    }) => Horse.patchHors(id, data))


    //Fns
    const getHorseWeight = (weight: number, metric: typeof user.unit_system) => {

        if (metric === 'metric') {
            return `${weight} ${t('units:kgShort')}`
        }
        if (metric === 'imperial') {
            return `${Math.round(weight / 2.2)} ${t('units:lbShort')}`
        }

    }

    const getHorseHeight = (height: number, metric: typeof user.unit_system) => {

        if (metric === 'metric') {
            return `${height} ${t('units:cmShort')}`
        }
        if (metric === 'imperial') {
            const hands = Math.round(height / 10.16); // Accurate + integer
            return `${hands} ${t('units:hands')}`;
        }

    }

    return {
        horse: horseQuery.data?.data,
        isHorseLoading: horseQuery.isLoading,
        isHorseError: horseQuery.isError,
        getHorseWeight,
        getHorseHeight,
        updateHorseHeightMutation,
        updateHorseWeightMutation,
        horseHeights: hydraTransform(horseHeightsQuery.data?.data ?? []),
        horseHeightsLoading: horseHeightsQuery.isLoading,
        horseWeightsLoading: horseWeightsQuery.isLoading,
        horseWeights: hydraTransform(horseWeightsQuery.data?.data ?? []),
        updateHorseHrMutation,
        updateHorseCommentMutation,
        updateHorseMutation
    }
}